/**
 * NotesTab — Threaded comments/notes attached to an entity
 *
 * For now, uses localStorage-backed notes (no GraphQL mutation yet).
 * When the backend Comment API is available, this switches to GraphQL.
 */

import { useState, useMemo, useCallback } from 'react';
import {
  Stack,
  Text,
  Group,
  Badge,
  Center,
  Textarea,
  Button,
  Paper,
  ActionIcon,
  Tooltip,
  Divider,
} from '@mantine/core';
import {
  IconSend,
  IconTrash,
  IconNotes,
} from '@tabler/icons-react';
import type { PrimitiveItem } from '@/hooks/usePrimitiveData';

// ============================================================================
// LOCAL STORAGE NOTES (until backend Comment API exists)
// ============================================================================

interface Note {
  id: string;
  text: string;
  author: string;
  timestamp: string;
}

function getStorageKey(entityId: string): string {
  return `ferp:entity-notes:${entityId}`;
}

function loadNotes(entityId: string): Note[] {
  try {
    const stored = localStorage.getItem(getStorageKey(entityId));
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function saveNotes(entityId: string, notes: Note[]): void {
  try {
    localStorage.setItem(getStorageKey(entityId), JSON.stringify(notes));
  } catch {
    // localStorage may be full
  }
}

// ============================================================================
// COMPONENT
// ============================================================================

interface NotesTabProps {
  entity: PrimitiveItem | null;
  primitiveType: string;
}

export function NotesTab({ entity }: NotesTabProps) {
  const entityId = entity?.id as string | undefined;
  const [notes, setNotes] = useState<Note[]>(() =>
    entityId ? loadNotes(entityId) : []
  );
  const [newNote, setNewNote] = useState('');

  // Reload notes when entity changes
  useMemo(() => {
    if (entityId) {
      setNotes(loadNotes(entityId));
    }
  }, [entityId]);

  const addNote = useCallback(() => {
    if (!entityId || !newNote.trim()) return;

    const note: Note = {
      id: `note-${Date.now()}`,
      text: newNote.trim(),
      author: 'Current User', // Would come from auth context
      timestamp: new Date().toISOString(),
    };

    const updated = [note, ...notes];
    setNotes(updated);
    saveNotes(entityId, updated);
    setNewNote('');
  }, [entityId, newNote, notes]);

  const deleteNote = useCallback((noteId: string) => {
    if (!entityId) return;
    const updated = notes.filter(n => n.id !== noteId);
    setNotes(updated);
    saveNotes(entityId, updated);
  }, [entityId, notes]);

  if (!entityId) {
    return (
      <Center py="xl">
        <Text c="dimmed" size="sm">Select an entity to view its notes.</Text>
      </Center>
    );
  }

  return (
    <Stack gap="sm">
      {/* Note input */}
      <Group align="flex-end" gap="xs">
        <Textarea
          placeholder="Add a note..."
          value={newNote}
          onChange={(e) => setNewNote(e.currentTarget.value)}
          minRows={2}
          maxRows={4}
          autosize
          style={{ flex: 1 }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              addNote();
            }
          }}
        />
        <Tooltip label="Add note (Ctrl+Enter)">
          <Button
            size="sm"
            leftSection={<IconSend size={14} />}
            onClick={addNote}
            disabled={!newNote.trim()}
          >
            Add
          </Button>
        </Tooltip>
      </Group>

      <Divider />

      {/* Notes list */}
      <Group justify="space-between">
        <Text size="xs" c="dimmed">{notes.length} note{notes.length !== 1 ? 's' : ''}</Text>
        <Badge size="xs" variant="light" color="gray">Local storage</Badge>
      </Group>

      {notes.length === 0 ? (
        <Center py="lg">
          <Stack align="center" gap="sm">
            <IconNotes size={32} color="var(--mantine-color-dimmed)" />
            <Text c="dimmed" size="sm">No notes yet. Add one above.</Text>
          </Stack>
        </Center>
      ) : (
        <Stack gap="xs">
          {notes.map((note) => (
            <Paper key={note.id} withBorder p="sm" radius="sm">
              <Group justify="space-between" mb={4}>
                <Group gap="xs">
                  <Text size="xs" fw={500}>{note.author}</Text>
                  <Text size="xs" c="dimmed">
                    {new Date(note.timestamp).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </Text>
                </Group>
                <Tooltip label="Delete note">
                  <ActionIcon
                    size="xs"
                    variant="subtle"
                    color="red"
                    onClick={() => deleteNote(note.id)}
                  >
                    <IconTrash size={12} />
                  </ActionIcon>
                </Tooltip>
              </Group>
              <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{note.text}</Text>
            </Paper>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
